import { certificates } from "../data/certificates.ts";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { FiArrowUpRight } from "react-icons/fi";

export default function Certificates() {
  useDocumentTitle("Certifications");

  return (
    <div className="py-24 px-6 text-light-text dark:text-text-primary">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-14 text-center">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-light-muted dark:text-text-faint">
            Certifications
          </p>
          <h1 className="mb-4 text-3xl md:text-4xl font-bold tracking-tight">
            Professional{" "}
            <span className="text-accent">Certifications</span>
          </h1>
          <p className="mx-auto max-w-2xl text-base md:text-lg text-light-muted dark:text-text-muted leading-relaxed">
            {certificates.length} earned certification, with 2 more in active
            preparation. All study notes are documented in{" "}
            <a
              href="https://brain.rakeshkadam.me/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-muted transition-colors underline underline-offset-2 decoration-accent/30"
            >
              my Digital Brain
            </a>
            .
          </p>
        </div>

        {/* Earned certifications */}
        <div className="mb-14">
          <p className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-light-muted dark:text-text-faint">
            Earned
          </p>

          <div className="space-y-5">
            {certificates.map((cert) => (
              <div
                key={cert.id}
                className="relative overflow-hidden rounded-xl border border-light-border bg-light-surface p-7 dark:border-border-subtle dark:bg-surface-1 transition-colors hover:border-accent/15"
              >
                <div className="absolute inset-y-0 left-0 w-1 bg-accent/50" />

                {/* Title row */}
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold">{cert.title}</h2>
                    <p className="mt-1 text-sm text-light-muted dark:text-text-muted">
                      {cert.issuer}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full border border-light-border bg-light-surface-2 px-3 py-1 font-mono text-xs text-light-muted dark:border-border-subtle dark:bg-surface-2 dark:text-text-faint">
                    {cert.date}
                  </span>
                </div>

                {/* Meta row */}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs text-light-muted dark:text-text-faint">
                  <span>ID: {cert.id}</span>
                  <span className="hidden sm:inline">|</span>
                  <span className="text-success dark:text-success">
                    Valid until {cert.validUntil}
                  </span>
                </div>

                <p className="mt-4 text-sm leading-relaxed text-light-muted dark:text-text-muted">
                  {cert.notes}
                </p>

                {/* Exam domains */}
                {cert.domains.length > 0 && (
                  <div className="mt-6">
                    <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-light-muted dark:text-text-faint">
                      Exam Domains
                    </p>
                    <div className="space-y-2.5">
                      {cert.domains.map((domain) => (
                        <div key={domain.name} className="flex items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-baseline justify-between gap-2">
                              <span className="truncate text-xs text-light-text dark:text-text-primary">
                                {domain.name}
                              </span>
                              <span className="shrink-0 font-mono text-[11px] tabular-nums text-light-muted dark:text-text-faint">
                                {domain.weight}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-light-surface-2 dark:bg-surface-2">
                              <div
                                className="h-full rounded-full bg-accent/70"
                                style={{ width: `${domain.weight}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Links */}
                <div className="mt-6 flex flex-wrap gap-3">
                  {cert.credlyLink && (
                    <a
                      href={cert.credlyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-1.5 rounded-lg bg-light-text dark:bg-text-primary px-5 py-2.5 text-sm font-semibold text-light-surface dark:text-surface-0 transition hover:bg-light-text-secondary dark:hover:bg-text-secondary"
                    >
                      View Credly Badge
                      <FiArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  )}
                  {cert.vaultNotesLink && (
                    <a
                      href={cert.vaultNotesLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-1.5 rounded-lg border border-light-border px-5 py-2.5 text-sm font-semibold text-light-text transition hover:border-accent/30 dark:border-border-subtle dark:text-text-primary dark:hover:border-accent/20"
                    >
                      View Prep Notes
                      <FiArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-accent" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* In Progress */}
        <div className="mb-14">
          <p className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-light-muted dark:text-text-faint">
            In Progress
          </p>

          <div className="space-y-5">
            <div className="relative overflow-hidden rounded-xl border border-dashed border-light-border bg-light-surface p-7 dark:border-border-subtle dark:bg-surface-1">
              <div className="absolute inset-y-0 left-0 w-1 bg-success/60" />

              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="mb-2 flex items-center gap-2.5">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
                    <span className="font-mono text-xs font-medium uppercase tracking-wider text-success">
                      Preparing
                    </span>
                  </div>
                  <h2 className="text-lg font-semibold">
                    Certified Kubernetes Administrator (CKA)
                  </h2>
                  <p className="mt-1 text-sm text-light-muted dark:text-text-muted">
                    Cloud Native Computing Foundation (CNCF)
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-light-muted dark:text-text-muted">
                Performance-based exam. 2 hours, 15-20 tasks in a live Kubernetes
                cluster covering cluster architecture, workloads, networking, storage,
                and troubleshooting. Currently on Day 23 of a 40-day structured prep
                course. All notes documented in my Digital Brain.
              </p>
            </div>

            <div className="relative overflow-hidden rounded-xl border border-dashed border-light-border bg-light-surface p-7 dark:border-border-subtle dark:bg-surface-1">
              <div className="absolute inset-y-0 left-0 w-1 bg-success/60" />

              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="mb-2 flex items-center gap-2.5">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
                    <span className="font-mono text-xs font-medium uppercase tracking-wider text-success">
                      Preparing
                    </span>
                  </div>
                  <h2 className="text-lg font-semibold">
                    Certified Kubernetes Security Specialist (CKS)
                  </h2>
                  <p className="mt-1 text-sm text-light-muted dark:text-text-muted">
                    Cloud Native Computing Foundation (CNCF)
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-light-muted dark:text-text-muted">
                Performance-based exam focused on Kubernetes cluster hardening,
                system hardening, supply chain security, monitoring and logging,
                and runtime security. Builds on CKA-level cluster administration.
                Planned after CKA completion.
              </p>
            </div>
          </div>
        </div>

        {/* Brain callout */}
        <div className="rounded-xl border border-accent/15 bg-light-surface p-7 text-center dark:bg-surface-1">
          <p className="mb-2 text-sm font-semibold text-accent">
            Digital Brain
          </p>
          <p className="mx-auto max-w-lg text-sm text-light-muted dark:text-text-muted leading-relaxed">
            All prep notes, study logs, and exam strategies are documented and
            searchable. Built with Quartz, updated as I prepare for each exam.
          </p>
          <a
            href="https://brain.rakeshkadam.me/"
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-4 inline-flex items-center gap-1 font-mono text-sm text-accent transition-colors hover:text-accent-muted"
          >
            brain.rakeshkadam.me
            <FiArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
